import React, { useState, useEffect } from 'react';
import { Modal, Input, Select, Button } from '../../../../components/ui';
import { Milestone } from '../hooks/useProjects';

interface MilestoneFormData {
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  status: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: MilestoneFormData) => void;
  milestone?: Milestone | null;
  loading?: boolean;
}

export const MilestoneModal: React.FC<Props> = ({ isOpen, onClose, onSubmit, milestone, loading }) => {
  const [formData, setFormData] = useState<MilestoneFormData>({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    status: 'PLANNED'
  });

  useEffect(() => {
    if (milestone) {
      setFormData({
        name: milestone.name ?? '',
        description: milestone.description ?? '',
        start_date: milestone.start_date ?? '',
        end_date: milestone.end_date ?? '',
        status: milestone.status ?? 'PLANNED'
      });
    } else {
      setFormData({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        status: 'PLANNED'
      });
    }
  }, [milestone, isOpen]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (field: keyof MilestoneFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [field]: e.target.value
    });
  };

  let submitButtonLabel: string;
  if (loading) {
    submitButtonLabel = 'Saving...';
  } else if (milestone) {
    submitButtonLabel = 'Update';
  } else {
    submitButtonLabel = 'Add';
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={milestone ? 'Edit Milestone' : 'Add Milestone'}
    >
      <form onSubmit={handleSubmit}>
        <Input
          label="Milestone Name"
          value={formData.name}
          onChange={handleChange('name')}
          placeholder="Enter milestone name"
          required
          className="mb-4"
        />

        <div className="mb-4">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description
          </label>
          <textarea id="description"
            value={formData.description}
            onChange={handleChange('description')}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
            placeholder="Enter milestone description"
          />
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <Input
            label="Start Date"
            type="date"
            value={formData.start_date}
            onChange={handleChange('start_date')}
            required
          />

          <Input
            label="End Date"
            type="date"
            value={formData.end_date}
            onChange={handleChange('end_date')}
            required
          />
        </div>

        <Select
          label="Status"
          value={formData.status}
          onChange={handleChange('status')}
          options={[
            { value: 'PLANNED', label: 'Planned' },
            { value: 'IN_PROGRESS', label: 'In Progress' },
            { value: 'COMPLETED', label: 'Completed' },
            { value: 'DELAYED', label: 'Delayed' }
          ]}
          className="mb-6"
        />

        <div className="flex justify-end space-x-3">
          <Button type="button" onClick={onClose} variant="secondary" disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {submitButtonLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
